import React, {useContext} from 'react'
import {ThemeContext} from '../contexts/ThemeContext'
import './Calendar.css';

/**
 * Calendar component displays a visual representation of tournament days
 * 
 * @param {Object} props - Component props
 * @param {Date} props.startDate - Tournament start date
 * @param {Date} props.endDate - Tournament end date
 */
const Calendar = ({ startDate, endDate }) => {
  const theme = useContext(ThemeContext)
  // Return null if no dates provided
  if (!startDate || !endDate) {
    return null;
  }

  // Convert string dates to Date objects if needed
  const tournamentStart = startDate instanceof Date ? startDate : new Date(startDate);
  const tournamentEnd = endDate instanceof Date ? endDate : new Date(endDate);
  
  // Get the current date and time for checking if tournament is active
  const now = new Date();
  
  // Check if tournament is currently active (including hours and minutes)
  const isTournamentActive = now >= tournamentStart && now <= tournamentEnd;
  
  // Create copies for date comparison with hours set to midnight
  const tournamentStartMidnight = new Date(tournamentStart);
  const tournamentEndMidnight = new Date(tournamentEnd);
  tournamentStartMidnight.setHours(0, 0, 0, 0);
  tournamentEndMidnight.setHours(0, 0, 0, 0);
  
  // Get today's date with hours set to midnight for day comparison
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  // Get the start of the week containing the tournament start date
  const weekStart = new Date(tournamentStartMidnight);
  weekStart.setDate(weekStart.getDate() - weekStart.getDay()); // Go back to Sunday
  
  // Get the end of the week containing the tournament end date
  const weekEnd = new Date(tournamentEndMidnight);
  const daysToAdd = 6 - weekEnd.getDay(); // Go forward to Saturday
  weekEnd.setDate(weekEnd.getDate() + daysToAdd);
  
  // Create array of weeks
  const weeks = [];
  let currentWeekStart = new Date(weekStart);
  
  while (currentWeekStart <= weekEnd) {
    const week = [];
    
    // Add days for this week
    for (let i = 0; i < 7; i++) {
      const currentDate = new Date(currentWeekStart);
      currentDate.setDate(currentDate.getDate() + i);
      
      // Check if this day is within the tournament period
      const isInTournament = currentDate >= tournamentStartMidnight && currentDate <= tournamentEndMidnight;
      
      // Check if this is today
      const isToday = currentDate.getTime() === today.getTime();
      
      // Check if this is tournament start or end day
      const isTournamentStartDay = currentDate.getDate() === tournamentStartMidnight.getDate() && 
                                  currentDate.getMonth() === tournamentStartMidnight.getMonth() && 
                                  currentDate.getFullYear() === tournamentStartMidnight.getFullYear();
      
      const isTournamentEndDay = currentDate.getDate() === tournamentEndMidnight.getDate() && 
                                currentDate.getMonth() === tournamentEndMidnight.getMonth() && 
                                currentDate.getFullYear() === tournamentEndMidnight.getFullYear();
      
      // Determine if this is a partial tournament day
      const isPartialStart = isTournamentStartDay && new Date(startDate).getHours() > 0;
      const isPartialEnd = isTournamentEndDay && new Date(endDate).getHours() < 23;
      
      // Determine day status for styling
      let dayStatus = '';
      if (isInTournament) {
        if (currentDate < today) {
          dayStatus = 'past';
        } else if (currentDate > today) {
          dayStatus = 'future';
        } else {
          // Only mark as 'today' if the tournament is currently active
          dayStatus = isTournamentActive ? 'today' : 'future';
        }
      }
      
      week.push({
        date: currentDate,
        isInTournament,
        isToday,
        isTournamentStartDay,
        isTournamentEndDay,
        isPartialStart,
        isPartialEnd,
        dayStatus,
        isTournamentActive
      });
    }
    
    // Check if this week has any tournament days
    const hasAnyTournamentDay = week.some(day => day.isInTournament);
    
    // Only add weeks that have at least one tournament day
    if (hasAnyTournamentDay) {
      weeks.push(week);
    }
    
    // Move to next week
    currentWeekStart.setDate(currentWeekStart.getDate() + 7);
  }
  
  // Day headers
  const dayHeaders = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  
  return (
    <div className="calendar-container">
      <div className="calendar">
        {/* Day headers */}
        {dayHeaders.map((day, index) => (
          <div key={`header-${index}`} className="calendar-day header">
            {day}
          </div>
        ))}
        
        {/* Calendar days */}
        {weeks.map((week, weekIndex) => (
          week.map((day, dayIndex) => {
            // Determine CSS classes
            let classes = 'calendar-day';
            
            if (day.isInTournament) {
              classes += ' tournament';
              
              if (day.dayStatus) {
                classes += ` ${day.dayStatus}`;
              }
            }
            
            // Determine the style for partial tournament days
            let style = {};
            
            if (day.isPartialStart) {
              // Right half is tournament color
              // Only use 'today' color if tournament is currently active
              const rightColor = (day.isToday && day.isTournamentActive) ? 
                'var(--tournament-bg)' : 
                day.dayStatus === 'past' ? 
                  'var(--tournament-past-bg)' : 
                  'var(--tournament-future-bg)';
              
              // Use theme-appropriate non-tournament background color
              const nonTournamentColor = theme === 'dark' ?
                'var(--dark-non-tournament-bg, #2a2a2a)' :
                'var(--non-tournament-bg, #f9f9f9)';
              style.background = `linear-gradient(to right, ${nonTournamentColor} 50%, ${rightColor} 50%)`;
            } else if (day.isPartialEnd) {
              // Left half is tournament color
              // Only use 'today' color if tournament is currently active
              const leftColor = (day.isToday && day.isTournamentActive) ? 
                'var(--tournament-bg)' : 
                day.dayStatus === 'past' ? 
                  'var(--tournament-past-bg)' : 
                  'var(--tournament-future-bg)';
              
              // Use theme-appropriate non-tournament background color
              const nonTournamentColor = theme === 'dark' ?
                'var(--dark-non-tournament-bg, #2a2a2a)' :
                'var(--non-tournament-bg, #f9f9f9)'

                style.background = `linear-gradient(to right, ${leftColor} 50%, ${nonTournamentColor} 50%)`;
            }
            
            return (
              <div 
                key={`day-${weekIndex}-${dayIndex}`} 
                className={classes}
                style={style}
              >
                {day.date.getDate()}
              </div>
            );
          })
        ))}
      </div>
    </div>
  );
};

export default Calendar;